import React from 'react';
import { Calendar, Clock, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { formatDate } from '../../../../../shared/utils/date';

interface EventsListProps {
  events: any[];
  loadingEvents: boolean;
  selectedEventId: string | null;
  onSelectEvent: (id: string) => void;
}

export function EventsList({ events, loadingEvents, selectedEventId, onSelectEvent }: EventsListProps) {
  return (
    <div style={{
      flex: 1,
      border: '1px solid var(--admin-card-border)',
      borderRadius: '0',
      backgroundColor: 'var(--admin-card-bg)',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      minHeight: 0
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ 
          fontFamily: 'Inter',
          fontWeight: 600,
          fontSize: '13px',
          lineHeight: '19.5px',
          letterSpacing: '0px',
          color: 'var(--admin-card-text)', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '6px' 
        }}>
          <Calendar size={14} style={{ color: '#3E8FA6' }} />
          Upcoming Meetings ({events.length})
        </span>
      </div>

      {loadingEvents ? (
        <div style={{ color: 'var(--admin-card-subtext)', fontSize: '12px', padding: '16px 0', textAlign: 'center' }}>Loading scheduled events...</div>
      ) : events.length === 0 ? (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          color: 'var(--admin-card-subtext)',
          textAlign: 'center',
          padding: '24px'
        }}>
          <Calendar size={32} style={{ color: '#3E8FA6' }} className="opacity-20 mb-2" />
          <span style={{ fontSize: '12px', fontWeight: 600 }}>No upcoming meetings found.</span>
          <span style={{ fontSize: '10px' }}>Make sure calendar permissions are active and click Sync.</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
          {events.map((e) => {
            const isSelected = selectedEventId === e.id;
            const date = new Date(e.start_time);
            const isToday = date.toDateString() === new Date().toDateString();
            const endD = new Date(e.end_time);
            const fakeLocalEnd = new Date(endD.getUTCFullYear(), endD.getUTCMonth(), endD.getUTCDate(), endD.getUTCHours(), endD.getUTCMinutes());
            const isPast = fakeLocalEnd < new Date();

            // Derive calendar source from external_event_id prefix
            const source = e.external_event_id?.startsWith('google_')
              ? 'google'
              : e.external_event_id?.startsWith('outlook_')
              ? 'outlook'
              : 'other';
            const sourceLabel = source === 'google' ? 'Google' : source === 'outlook' ? 'Outlook' : 'Calendar';
            const sourceBg   = source === 'google'  ? 'rgba(234,67,53,0.10)'  : source === 'outlook' ? 'rgba(0,114,206,0.10)' : 'rgba(100,116,139,0.10)';
            const sourceBorder = source === 'google' ? 'rgba(234,67,53,0.25)'  : source === 'outlook' ? 'rgba(0,114,206,0.25)' : 'rgba(100,116,139,0.25)';
            const sourceColor  = source === 'google' ? '#ea4335'               : source === 'outlook' ? '#0072ce'              : '#64748b';

            return (
              <div
                key={e.id}
                onClick={() => onSelectEvent(e.id)}
                style={{
                  padding: '8px 10px',
                  borderRadius: '0',
                  backgroundColor: isSelected ? 'rgba(62, 143, 166, 0.08)' : 'transparent',
                  border: isSelected ? '1px solid #3E8FA6' : '1px solid var(--admin-card-border)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  opacity: isPast ? 0.75 : 1
                }}
                className="event-list-item"
              >
                {/* Title row + Intel Ready badge */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <span style={{ 
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: '13px',
                    lineHeight: '18px',
                    letterSpacing: '-0.48px',
                    color: 'var(--admin-card-text)'
                  }}>
                    {e.title}
                  </span>
                  {e.has_briefing && (
                    <span style={{
                      fontSize: '9px',
                      fontWeight: 800,
                      color: '#10b981',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      padding: '1px 6px',
                      borderRadius: '0',
                      flexShrink: 0
                    }}>
                      Intel Ready
                    </span>
                  )}
                </div>

                {/* Time row */}
                <span style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Clock size={10} />
                  {formatDate(e.start_time, true, true)}
                </span>

                {/* Source badge + prospect row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {e.prospect_email || 'No attendees listed'}
                  </div>
                  <span style={{
                    fontSize: '8px',
                    fontWeight: 800,
                    color: sourceColor,
                    backgroundColor: sourceBg,
                    border: `1px solid ${sourceBorder}`,
                    padding: '1px 6px',
                    borderRadius: '0',
                    flexShrink: 0,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase'
                  }}>
                    {sourceLabel}
                  </span>
                </div>
              </div>
            );
          })}

        </div>
      )}
    </div>
  );
}
